import {useIntl} from 'react-intl';
import {ReactNode, useEffect, useMemo, useState} from 'react';
import {useQueryClient} from 'react-query';
import {DateTime} from 'luxon';
import {Alert, Card, Button as BootstrapButton, Col, Form, Modal, Row} from 'react-bootstrap';
import ActionIcon from '../../../../../Components/ActionIcon';
import Badge from '../../../../../app/common/components/Badge';
import Button from '../../../../../Components/Button';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import {
  JourSemaine,
  PeriodeActivite,
  Periodicite,
  PlageHoraireDetail,
  PlageHoraireGroupe,
} from '../TrunkSip/mocks';
import {useGetPlagesHoraires, useGetVirtualPbxNumeros} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import Loading from '../../../../../app/common/components/Loading';

const TYPE_BADGE_KEYS: Record<PlageHoraireGroupe['type'], string> = {
  ouverture: 'TELEPHONIE.PBX.PLAGES.BADGE_OUVERTURE',
  vacances: 'TELEPHONIE.PBX.PLAGES.BADGE_VACANCES',
};

const TYPE_BADGE_COLOR: Record<PlageHoraireGroupe['type'], string> = {
  ouverture: 'primary',
  vacances: 'warning',
};

const TYPE_ICON: Record<PlageHoraireGroupe['type'], string> = {
  ouverture: 'bi-calendar2-week',
  vacances: 'bi-airplane',
};

const JOURS_ORDRE: JourSemaine[] = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

const JOUR_LETTRE_KEYS: Record<JourSemaine, string> = {
  lun: 'TELEPHONIE.PBX.PLAGES.JOUR.LUN',
  mar: 'TELEPHONIE.PBX.PLAGES.JOUR.MAR',
  mer: 'TELEPHONIE.PBX.PLAGES.JOUR.MER',
  jeu: 'TELEPHONIE.PBX.PLAGES.JOUR.JEU',
  ven: 'TELEPHONIE.PBX.PLAGES.JOUR.VEN',
  sam: 'TELEPHONIE.PBX.PLAGES.JOUR.SAM',
  dim: 'TELEPHONIE.PBX.PLAGES.JOUR.DIM',
};

const JOUR_NOM_KEYS: Record<JourSemaine, string> = {
  lun: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.LUN',
  mar: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.MAR',
  mer: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.MER',
  jeu: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.JEU',
  ven: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.VEN',
  sam: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.SAM',
  dim: 'TELEPHONIE.PBX.PLAGES.JOUR_NOM.DIM',
};

const PERIODICITE_LABEL_KEYS: Record<Periodicite, string> = {
  aucune: 'TELEPHONIE.PBX.PLAGES.PERIODICITE.AUCUNE',
  semaine: 'TELEPHONIE.PBX.PLAGES.PERIODICITE.SEMAINE',
  mois: 'TELEPHONIE.PBX.PLAGES.PERIODICITE.MOIS',
  annee: 'TELEPHONIE.PBX.PLAGES.PERIODICITE.ANNEE',
};

const PERIODICITES: Periodicite[] = ['aucune', 'semaine', 'mois', 'annee'];

const MAX_PERIODES = 6;

const formatDepuis = (iso: string, locale: string): string =>
  DateTime.fromISO(iso).setLocale(locale).toFormat("d/MM/yyyy 'à' HH:mm");

type StatutValidite = 'futur' | 'en_cours' | 'passe';

const STATUT_VALIDITE_KEYS: Record<StatutValidite, string> = {
  futur: 'TELEPHONIE.PBX.PLAGES.VALIDITE.FUTUR',
  en_cours: 'TELEPHONIE.PBX.PLAGES.VALIDITE.EN_COURS',
  passe: 'TELEPHONIE.PBX.PLAGES.VALIDITE.PASSE',
};

const calculerStatutValidite = (plage: PlageHoraireDetail): StatutValidite => {
  const maintenant = DateTime.now();
  const depuis = DateTime.fromISO(plage.depuis);
  const finLe = plage.finLe ? DateTime.fromISO(plage.finLe) : null;
  if (depuis > maintenant) {
    return 'futur';
  }
  if (finLe && finLe < maintenant) {
    return 'passe';
  }
  return 'en_cours';
};

const ValiditePlage = ({plage}: {plage: PlageHoraireDetail}): JSX.Element => {
  const intl = useIntl();
  const statut = calculerStatutValidite(plage);
  return (
    <div className={`fs-7 ${statut === 'en_cours' ? 'text-success fw-semibold' : 'text-muted fst-italic'}`}>
      {intl.formatMessage({id: STATUT_VALIDITE_KEYS[statut]})} —{' '}
      {intl.formatMessage(
        {id: 'TELEPHONIE.PBX.PLAGES.DU_AU'},
        {
          debut: formatDepuis(plage.depuis, intl.locale),
          fin: plage.finLe
            ? formatDepuis(plage.finLe, intl.locale)
            : intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.INDETERMINE'}),
        }
      )}
    </div>
  );
};

export const JoursChips = ({jours}: {jours: JourSemaine[]}): JSX.Element => {
  const intl = useIntl();
  return (
    <div className="d-flex gap-1">
      {JOURS_ORDRE.map((jour) => (
        <span
          key={jour}
          className={`d-flex align-items-center justify-content-center rounded fw-bold fs-8 ${
            jours.includes(jour) ? 'bg-primary text-white' : 'bg-light text-muted'
          }`}
          style={{width: 24, height: 24}}
        >
          {intl.formatMessage({id: JOUR_LETTRE_KEYS[jour]})}
        </span>
      ))}
    </div>
  );
};

const toMinutes = (heure: string): number => {
  const [h, m] = heure.split(':').map(Number);
  return h * 60 + (m || 0);
};

export const TimelinePlage = ({periodes}: {periodes: PeriodeActivite[]}): JSX.Element => {
  const totalMinutes = 24 * 60;

  return (
    <div>
      <div className="d-flex justify-content-between mb-1 px-1">
        <i className="bi bi-moon-stars-fill text-muted fs-7" />
        <i className="bi bi-sun-fill text-warning fs-6" />
        <i className="bi bi-moon-stars-fill text-muted fs-7" />
      </div>
      <div className="position-relative bg-light rounded" style={{height: 10}}>
        {periodes.map((periode, index) => {
          const debut = toMinutes(periode.heureDebut);
          const fin = toMinutes(periode.heureFin);
          const left = (debut / totalMinutes) * 100;
          const width = Math.max(0, ((fin - debut) / totalMinutes) * 100);
          return (
            <div
              key={index}
              className="position-absolute bg-primary rounded"
              style={{height: 10, left: `${left}%`, width: `${width}%`}}
            />
          );
        })}
      </div>
      <div className="d-flex justify-content-between text-muted fs-8 mt-1">
        <span>00:00</span>
        <span>12:00</span>
        <span>23:59</span>
      </div>
    </div>
  );
};

interface GroupeFormState {
  nom: string;
  type: PlageHoraireGroupe['type'];
}

interface PlageFormState {
  nom: string;
  periodicite: Periodicite;
  jours: JourSemaine[];
  periodes: PeriodeActivite[];
  debutDate: string;
  debutHeure: string;
  finActive: boolean;
  finDate: string;
  finHeure: string;
}

const PLAGE_FORM_VIDE: PlageFormState = {
  nom: '',
  periodicite: 'semaine',
  jours: [],
  periodes: [{heureDebut: '', heureFin: ''}],
  debutDate: '',
  debutHeure: '',
  finActive: false,
  finDate: '',
  finHeure: '',
};

interface PlagesHorairesProps {
  ouvrirAjoutGroupeInitial?: boolean;
  onOuvrirAjoutGroupeConsomme?: () => void;
}

const PlagesHoraires = ({
  ouvrirAjoutGroupeInitial = false,
  onOuvrirAjoutGroupeConsomme,
}: PlagesHorairesProps): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data, isLoading} = useGetPlagesHoraires();
  const {data: numerosData} = useGetVirtualPbxNumeros();
  const [groupesLocal, setGroupesLocal] = useState<PlageHoraireGroupe[] | null>(null);
  const [message, setMessage] = useAutoDismissMessage();
  const [erreurSuppressionGroupe, setErreurSuppressionGroupe] = useState<{
    groupe: string;
    numero: string;
  } | null>(null);
  const [detailsOuverts, setDetailsOuverts] = useState<Set<number>>(new Set());

  const [groupeEnEdition, setGroupeEnEdition] = useState<PlageHoraireGroupe | null>(null);
  const [showGroupeForm, setShowGroupeForm] = useState(false);
  const [groupeForm, setGroupeForm] = useState<GroupeFormState>({nom: '', type: 'ouverture'});
  const [groupeASupprimer, setGroupeASupprimer] = useState<PlageHoraireGroupe | null>(null);
  const [groupeInlineId, setGroupeInlineId] = useState<number | null>(null);
  const [groupeInlineForm, setGroupeInlineForm] = useState<GroupeFormState>({
    nom: '',
    type: 'ouverture',
  });

  const [groupeCiblePourPlage, setGroupeCiblePourPlage] = useState<PlageHoraireGroupe | null>(
    null
  );
  const [plageEnEdition, setPlageEnEdition] = useState<PlageHoraireDetail | null>(null);
  const [showPlageForm, setShowPlageForm] = useState(false);
  const [plageForm, setPlageForm] = useState<PlageFormState>(PLAGE_FORM_VIDE);
  const [plageASupprimer, setPlageASupprimer] = useState<{
    groupe: PlageHoraireGroupe;
    plage: PlageHoraireDetail;
  } | null>(null);

  useEffect(() => {
    if (data) {
      setGroupesLocal(data.groupes);
    }
  }, [data]);

  const groupes = useMemo(() => groupesLocal ?? data?.groupes ?? [], [groupesLocal, data]);

  const mettreAJourGroupes = (groupesMisAJour: PlageHoraireGroupe[]): void => {
    setGroupesLocal(groupesMisAJour);
    queryClient.setQueryData('centrex-pbx-plages-horaires', {groupes: groupesMisAJour});
  };

  const basculerDetails = (id: number): void => {
    setDetailsOuverts((precedent) => {
      const suivant = new Set(precedent);
      if (suivant.has(id)) {
        suivant.delete(id);
      } else {
        suivant.add(id);
      }
      return suivant;
    });
  };

  const ouvrirAjoutGroupe = (): void => {
    setGroupeEnEdition(null);
    setGroupeForm({nom: '', type: 'ouverture'});
    setShowGroupeForm(true);
  };

  useEffect(() => {
    if (ouvrirAjoutGroupeInitial) {
      ouvrirAjoutGroupe();
      onOuvrirAjoutGroupeConsomme?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvrirAjoutGroupeInitial]);

  const ouvrirEditionInlineGroupe = (groupe: PlageHoraireGroupe): void => {
    setGroupeInlineId(groupe.id);
    setGroupeInlineForm({nom: groupe.nom, type: groupe.type});
  };

  const annulerEditionInlineGroupe = (): void => {
    setGroupeInlineId(null);
  };

  const enregistrerGroupeInline = (): void => {
    if (groupeInlineId == null) {
      return;
    }
    mettreAJourGroupes(
      groupes.map((groupe) =>
        groupe.id === groupeInlineId
          ? {...groupe, nom: groupeInlineForm.nom.trim(), type: groupeInlineForm.type}
          : groupe
      )
    );
    setGroupeInlineId(null);
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  const enregistrerGroupe = (): void => {
    if (groupeEnEdition) {
      mettreAJourGroupes(
        groupes.map((groupe) =>
          groupe.id === groupeEnEdition.id
            ? {...groupe, nom: groupeForm.nom.trim(), type: groupeForm.type}
            : groupe
        )
      );
    } else {
      const nouvelId = Math.max(0, ...groupes.map((groupe) => groupe.id)) + 1;
      mettreAJourGroupes([
        ...groupes,
        {id: nouvelId, nom: groupeForm.nom.trim(), type: groupeForm.type, plages: []},
      ]);
    }
    setShowGroupeForm(false);
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  const numeroUtilisantGroupe = (nomGroupe: string) =>
    (numerosData?.numerosGlobaux ?? []).find((numero) =>
      numero.plages.some((plage) => plage.nomPlage === nomGroupe)
    );

  const nomGroupeDejaUtilise = (nom: string, idExclu: number | null): boolean =>
    groupes.some(
      (groupe) =>
        groupe.id !== idExclu && groupe.nom.trim().toLowerCase() === nom.trim().toLowerCase()
    );

  const demanderSuppressionGroupe = (groupe: PlageHoraireGroupe): void => {
    const numero = numeroUtilisantGroupe(groupe.nom);
    if (numero) {
      setErreurSuppressionGroupe({groupe: groupe.nom, numero: numero.numeroFormate});
      return;
    }
    setErreurSuppressionGroupe(null);
    setGroupeASupprimer(groupe);
  };

  const confirmerSuppressionGroupe = (): void => {
    if (groupeASupprimer) {
      mettreAJourGroupes(groupes.filter((groupe) => groupe.id !== groupeASupprimer.id));
      setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
    }
    setGroupeASupprimer(null);
  };

  const ouvrirAjoutPlage = (groupe: PlageHoraireGroupe): void => {
    setGroupeCiblePourPlage(groupe);
    setPlageEnEdition(null);
    const maintenant = DateTime.now();
    setPlageForm({
      ...PLAGE_FORM_VIDE,
      periodes: [{heureDebut: '', heureFin: ''}],
      debutDate: maintenant.toFormat('yyyy-MM-dd'),
      debutHeure: maintenant.toFormat('HH:mm'),
    });
    setShowPlageForm(true);
  };

  const ouvrirModificationPlage = (groupe: PlageHoraireGroupe, plage: PlageHoraireDetail): void => {
    setGroupeCiblePourPlage(groupe);
    setPlageEnEdition(plage);
    const depuisDt = DateTime.fromISO(plage.depuis);
    const finDt = plage.finLe ? DateTime.fromISO(plage.finLe) : null;
    setPlageForm({
      nom: plage.nom,
      periodicite: plage.periodicite,
      jours: plage.jours ?? [],
      periodes: plage.periodes.length > 0 ? plage.periodes : [{heureDebut: '', heureFin: ''}],
      debutDate: depuisDt.toFormat('yyyy-MM-dd'),
      debutHeure: depuisDt.toFormat('HH:mm'),
      finActive: finDt != null,
      finDate: finDt ? finDt.toFormat('yyyy-MM-dd') : '',
      finHeure: finDt ? finDt.toFormat('HH:mm') : '',
    });
    setShowPlageForm(true);
  };

  const basculerJourForm = (jour: JourSemaine): void => {
    setPlageForm((precedent) => ({
      ...precedent,
      jours: precedent.jours.includes(jour)
        ? precedent.jours.filter((j) => j !== jour)
        : [...precedent.jours, jour],
    }));
  };

  const ajouterPeriodeForm = (): void => {
    setPlageForm((precedent) =>
      precedent.periodes.length >= MAX_PERIODES
        ? precedent
        : {...precedent, periodes: [...precedent.periodes, {heureDebut: '', heureFin: ''}]}
    );
  };

  const supprimerPeriodeForm = (index: number): void => {
    setPlageForm((precedent) => ({
      ...precedent,
      periodes: precedent.periodes.filter((_, i) => i !== index),
    }));
  };

  const modifierPeriodeForm = (
    index: number,
    champ: 'heureDebut' | 'heureFin',
    valeur: string
  ): void => {
    setPlageForm((precedent) => ({
      ...precedent,
      periodes: precedent.periodes.map((periode, i) =>
        i === index ? {...periode, [champ]: valeur} : periode
      ),
    }));
  };

  const periodesValides = plageForm.periodes.filter(
    (periode) => periode.heureDebut !== '' && periode.heureFin !== ''
  );

  const plageFormValide =
    plageForm.nom.trim() !== '' &&
    periodesValides.length > 0 &&
    plageForm.debutDate !== '' &&
    plageForm.debutHeure !== '';

  const enregistrerPlage = (): void => {
    if (!groupeCiblePourPlage) {
      return;
    }
    const valeurs: Omit<PlageHoraireDetail, 'id'> = {
      nom: plageForm.nom.trim(),
      depuis: `${plageForm.debutDate}T${plageForm.debutHeure}:00`,
      finLe:
        plageForm.finActive && plageForm.finDate && plageForm.finHeure
          ? `${plageForm.finDate}T${plageForm.finHeure}:00`
          : undefined,
      periodicite: plageForm.periodicite,
      jours: plageForm.periodicite === 'semaine' ? plageForm.jours : undefined,
      periodes: periodesValides,
    };

    mettreAJourGroupes(
      groupes.map((groupe) => {
        if (groupe.id !== groupeCiblePourPlage.id) {
          return groupe;
        }
        if (plageEnEdition) {
          return {
            ...groupe,
            plages: groupe.plages.map((plage) =>
              plage.id === plageEnEdition.id ? {id: plage.id, ...valeurs} : plage
            ),
          };
        }
        const nouvelId = Math.max(0, ...groupe.plages.map((plage) => plage.id)) + 1;
        return {...groupe, plages: [...groupe.plages, {id: nouvelId, ...valeurs}]};
      })
    );
    setShowPlageForm(false);
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  const confirmerSuppressionPlage = (): void => {
    if (plageASupprimer) {
      mettreAJourGroupes(
        groupes.map((groupe) =>
          groupe.id === plageASupprimer.groupe.id
            ? {...groupe, plages: groupe.plages.filter((plage) => plage.id !== plageASupprimer.plage.id)}
            : groupe
        )
      );
      setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
    }
    setPlageASupprimer(null);
  };

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-5">
        <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.TITLE'})} />
        <div className="d-flex align-items-center gap-3">
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.ADD_GROUPE'})}
            faIcon="bi bi-plus-lg fs-6 me-2"
            onClick={ouvrirAjoutGroupe}
          />
          <ActionIcon
            label={intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.PRINT'})}
            faIcon="fs-3 bi bi-printer"
            onClick={() => window.print()}
          />
        </div>
      </div>

      <Card bg="light" className="mb-5">
        <Card.Body className="d-flex align-items-center gap-4 py-4">
          <i className="bi bi-info-circle-fill text-primary fs-2x flex-shrink-0" />
          <span className="fs-6">{intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.INFO'})}</span>
        </Card.Body>
      </Card>

      {message && <div className="fw-bold text-danger mb-3">{message}</div>}
      {erreurSuppressionGroupe && (
        <Alert variant="danger" onClose={() => setErreurSuppressionGroupe(null)} dismissible>
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.PLAGES.GROUPE_EN_USAGE'},
            {
              groupe: erreurSuppressionGroupe.groupe,
              numero: erreurSuppressionGroupe.numero,
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Alert>
      )}

      <Row className="g-5">
        {groupes.map((groupe) => (
          <Col key={groupe.id} xs={12} xl={6}>
            <Card className="h-100">
              <Card.Header
                className={`d-flex justify-content-between flex-wrap gap-3 ${
                  groupeInlineId === groupe.id ? 'align-items-start py-4' : 'align-items-center'
                }`}
              >
                {groupeInlineId === groupe.id ? (
                  <div className="w-100 d-flex flex-column gap-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                      <div>
                        <Form.Control
                          style={{maxWidth: 260}}
                          value={groupeInlineForm.nom}
                          onChange={(e) =>
                            setGroupeInlineForm({...groupeInlineForm, nom: e.target.value})
                          }
                        />
                        {nomGroupeDejaUtilise(groupeInlineForm.nom, groupe.id) && (
                          <div className="text-danger fs-8 mt-1">
                            {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.GROUPE_FORM.NOM_DUPLIQUE'})}
                          </div>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-4">
                        <Form.Check
                          type="radio"
                          id={`groupe-inline-type-vacances-${groupe.id}`}
                          name={`groupe-inline-type-${groupe.id}`}
                          label={intl.formatMessage({id: TYPE_BADGE_KEYS.vacances})}
                          checked={groupeInlineForm.type === 'vacances'}
                          onChange={() =>
                            setGroupeInlineForm({...groupeInlineForm, type: 'vacances'})
                          }
                        />
                        <Form.Check
                          type="radio"
                          id={`groupe-inline-type-ouverture-${groupe.id}`}
                          name={`groupe-inline-type-${groupe.id}`}
                          label={intl.formatMessage({id: TYPE_BADGE_KEYS.ouverture})}
                          checked={groupeInlineForm.type === 'ouverture'}
                          onChange={() =>
                            setGroupeInlineForm({...groupeInlineForm, type: 'ouverture'})
                          }
                        />
                      </div>
                    </div>
                    <div className="d-flex gap-3">
                      <BootstrapButton
                        variant="secondary"
                        size="sm"
                        onClick={annulerEditionInlineGroupe}
                      >
                        {intl.formatMessage({id: 'CANCEL'})}
                      </BootstrapButton>
                      <BootstrapButton
                        variant="success"
                        size="sm"
                        disabled={
                          groupeInlineForm.nom.trim() === '' ||
                          nomGroupeDejaUtilise(groupeInlineForm.nom, groupe.id)
                        }
                        onClick={enregistrerGroupeInline}
                      >
                        {intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
                      </BootstrapButton>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center gap-3">
                      <span
                        className={`d-flex align-items-center justify-content-center rounded-circle bg-light-${TYPE_BADGE_COLOR[groupe.type]} text-${TYPE_BADGE_COLOR[groupe.type]} flex-shrink-0`}
                        style={{width: 36, height: 36}}
                      >
                        <i className={`bi ${TYPE_ICON[groupe.type]}`} />
                      </span>
                      <Card.Title className="m-0">
                        <Level3Title title={groupe.nom} />
                      </Card.Title>
                      <Badge
                        text={intl.formatMessage({id: TYPE_BADGE_KEYS[groupe.type]})}
                        color={TYPE_BADGE_COLOR[groupe.type]}
                        light
                        size="small"
                      />
                    </div>
                    <div>
                      <ActionIcon
                        label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                        faIcon="fs-3 bi bi-pencil"
                        onClick={() => ouvrirEditionInlineGroupe(groupe)}
                      />
                      <ActionIcon
                        label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                        faIcon="fs-3 bi bi-trash text-danger"
                        onClick={() => demanderSuppressionGroupe(groupe)}
                      />
                    </div>
                  </>
                )}
              </Card.Header>
              <Card.Body>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                  <span className="text-muted fs-6">
                    {intl.formatMessage(
                      {id: 'TELEPHONIE.PBX.PLAGES.DECLAREES'},
                      {count: groupe.plages.length}
                    )}
                  </span>
                  <div className="d-flex gap-3">
                    <BootstrapButton
                      variant="light-primary"
                      size="sm"
                      onClick={() => basculerDetails(groupe.id)}
                    >
                      {intl.formatMessage({
                        id: detailsOuverts.has(groupe.id)
                          ? 'TELEPHONIE.PBX.PLAGES.MASQUER'
                          : 'TELEPHONIE.PBX.PLAGES.DETAILS',
                      })}
                    </BootstrapButton>
                    <BootstrapButton
                      variant="primary"
                      size="sm"
                      onClick={() => ouvrirAjoutPlage(groupe)}
                    >
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.ADD_PLAGE'})}
                    </BootstrapButton>
                  </div>
                </div>

                {detailsOuverts.has(groupe.id) && (
                  <div className="border-top pt-4">
                    {groupe.plages.length === 0 && (
                      <div className="text-danger fw-semibold fs-6">
                        {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.NO_PLAGES'})}
                      </div>
                    )}
                    <div className="d-flex flex-column gap-3">
                      {groupe.plages.map((plage) => (
                        <Card key={plage.id} className="border">
                          <Card.Body className="p-3">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                              <div className="d-flex align-items-center gap-2">
                                <i className="bi bi-clock-history text-primary fs-5" />
                                <span className="fw-semibold fs-6">{plage.nom}</span>
                              </div>
                              <BootstrapButton
                                variant="primary"
                                size="sm"
                                onClick={() => ouvrirModificationPlage(groupe, plage)}
                              >
                                <i className="bi bi-pencil me-2" />
                                {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                              </BootstrapButton>
                            </div>
                            <ValiditePlage plage={plage} />

                            {plage.periodicite === 'semaine' && (
                              <JoursChips jours={plage.jours ?? []} />
                            )}
                            <div className="mt-3">
                              <TimelinePlage periodes={plage.periodes} />
                            </div>

                            <div className="d-flex justify-content-end mt-2">
                              <ActionIcon
                                label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                                faIcon="fs-4 bi bi-trash text-danger"
                                onClick={() => setPlageASupprimer({groupe, plage})}
                              />
                            </div>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Modal show={showGroupeForm} onHide={() => setShowGroupeForm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({
              id: groupeEnEdition
                ? 'TELEPHONIE.PBX.PLAGES.GROUPE_FORM.TITLE_EDIT'
                : 'TELEPHONIE.PBX.PLAGES.GROUPE_FORM.TITLE_ADD',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-4">
            <Col xs={12}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.GROUPE_FORM.NOM'})}
              </Form.Label>
              <Form.Control
                value={groupeForm.nom}
                onChange={(e) => setGroupeForm({...groupeForm, nom: e.target.value})}
              />
              {nomGroupeDejaUtilise(groupeForm.nom, groupeEnEdition?.id ?? null) && (
                <div className="text-danger fs-8 mt-1">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.GROUPE_FORM.NOM_DUPLIQUE'})}
                </div>
              )}
            </Col>
            <Col xs={12}>
              <Form.Label className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.GROUPE_FORM.TYPE'})}
              </Form.Label>
              <Form.Select
                value={groupeForm.type}
                onChange={(e) =>
                  setGroupeForm({
                    ...groupeForm,
                    type: e.target.value as PlageHoraireGroupe['type'],
                  })
                }
              >
                <option value="ouverture">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.TYPE.OUVERTURE'})}
                </option>
                <option value="vacances">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.TYPE.VACANCES'})}
                </option>
              </Form.Select>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setShowGroupeForm(false)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
            disabled={
              groupeForm.nom.trim() === '' ||
              nomGroupeDejaUtilise(groupeForm.nom, groupeEnEdition?.id ?? null)
            }
            onClick={enregistrerGroupe}
          />
        </Modal.Footer>
      </Modal>

      <Modal show={groupeASupprimer != null} onHide={() => setGroupeASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.PLAGES.GROUPE_DELETE.BODY'},
            {
              nom: groupeASupprimer?.nom ?? '',
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setGroupeASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppressionGroupe}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={showPlageForm} onHide={() => setShowPlageForm(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({
              id: plageEnEdition
                ? 'TELEPHONIE.PBX.PLAGES.PLAGE_FORM.TITLE_EDIT'
                : 'TELEPHONIE.PBX.PLAGES.PLAGE_FORM.TITLE_ADD',
            })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="mb-5">
                <div className="fw-bold text-uppercase fs-7 mb-1">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.SECTION.INTITULE'})}
                </div>
                <div className="text-muted fs-7 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.SECTION.INTITULE_DESC'})}
                </div>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.PLAGE_FORM.NOM'})}
                </Form.Label>
                <Form.Control
                  value={plageForm.nom}
                  onChange={(e) => setPlageForm({...plageForm, nom: e.target.value})}
                />
              </div>

              <div className="mb-5">
                <div className="fw-bold text-uppercase fs-7 mb-1">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.SECTION.PERIODES'})}
                </div>
                <div className="text-muted fs-7 mb-3">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.SECTION.PERIODES_DESC'})}
                </div>
                {plageForm.periodes.map((periode, index) => (
                  <div key={index} className="d-flex align-items-center gap-2 mb-2">
                    <span className="fs-6" style={{minWidth: 100}}>
                      {intl.formatMessage(
                        {id: 'TELEPHONIE.PBX.PLAGES.PERIODE_N'},
                        {n: index + 1}
                      )}
                    </span>
                    <Form.Control
                      type="time"
                      size="sm"
                      style={{maxWidth: 130}}
                      value={periode.heureDebut}
                      onChange={(e) => modifierPeriodeForm(index, 'heureDebut', e.target.value)}
                    />
                    <span className="fs-6">{intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.A'})}</span>
                    <Form.Control
                      type="time"
                      size="sm"
                      style={{maxWidth: 130}}
                      value={periode.heureFin}
                      onChange={(e) => modifierPeriodeForm(index, 'heureFin', e.target.value)}
                    />
                    {plageForm.periodes.length > 1 && (
                      <ActionIcon
                        label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                        faIcon="fs-5 bi bi-trash text-danger"
                        onClick={() => supprimerPeriodeForm(index)}
                      />
                    )}
                  </div>
                ))}
                {plageForm.periodes.length < MAX_PERIODES && (
                  <BootstrapButton variant="light-primary" size="sm" onClick={ajouterPeriodeForm}>
                    <i className="bi bi-plus-lg me-2" />
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.AJOUTER_PERIODE'})}
                  </BootstrapButton>
                )}
              </div>

              <div className="mb-5">
                <div className="fw-bold text-uppercase fs-7 mb-3">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.SECTION.PERIODICITE'})}
                </div>
                <div className="d-flex flex-wrap gap-5">
                  <div className="d-flex flex-column gap-2">
                    {PERIODICITES.map((valeur) => (
                      <Form.Check
                        key={valeur}
                        type="radio"
                        id={`periodicite-${valeur}`}
                        name="periodicite"
                        label={intl.formatMessage({id: PERIODICITE_LABEL_KEYS[valeur]})}
                        checked={plageForm.periodicite === valeur}
                        onChange={() => setPlageForm({...plageForm, periodicite: valeur})}
                      />
                    ))}
                  </div>
                  {plageForm.periodicite === 'semaine' && (
                    <div className="d-flex flex-wrap gap-4 align-items-start border-start ps-5">
                      {JOURS_ORDRE.map((jour) => (
                        <Form.Check
                          key={jour}
                          type="checkbox"
                          id={`jour-form-${jour}`}
                          label={intl.formatMessage({id: JOUR_NOM_KEYS[jour]})}
                          checked={plageForm.jours.includes(jour)}
                          onChange={() => basculerJourForm(jour)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-5">
                <div className="fw-bold text-uppercase fs-7 mb-3">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.SECTION.ACTIVATION'})}
                </div>
                <Row className="g-3 align-items-center mb-3">
                  <Col xs="auto" className="fs-6" style={{minWidth: 90}}>
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.DEBUT_LE'})}
                  </Col>
                  <Col xs="auto">
                    <Form.Control
                      type="date"
                      size="sm"
                      value={plageForm.debutDate}
                      onChange={(e) => setPlageForm({...plageForm, debutDate: e.target.value})}
                    />
                  </Col>
                  <Col xs="auto" className="fs-6">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.A'})}
                  </Col>
                  <Col xs="auto">
                    <Form.Control
                      type="time"
                      size="sm"
                      value={plageForm.debutHeure}
                      onChange={(e) => setPlageForm({...plageForm, debutHeure: e.target.value})}
                    />
                  </Col>
                </Row>
                <Row className="g-3 align-items-center">
                  <Col xs="auto">
                    <Form.Check
                      type="checkbox"
                      id="fin-active"
                      label={intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.FIN_LE'})}
                      checked={plageForm.finActive}
                      onChange={(e) => setPlageForm({...plageForm, finActive: e.target.checked})}
                    />
                  </Col>
                  <Col xs="auto">
                    <Form.Control
                      type="date"
                      size="sm"
                      disabled={!plageForm.finActive}
                      value={plageForm.finDate}
                      onChange={(e) => setPlageForm({...plageForm, finDate: e.target.value})}
                    />
                  </Col>
                  <Col xs="auto" className="fs-6">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.A'})}
                  </Col>
                  <Col xs="auto">
                    <Form.Control
                      type="time"
                      size="sm"
                      disabled={!plageForm.finActive}
                      value={plageForm.finHeure}
                      onChange={(e) => setPlageForm({...plageForm, finHeure: e.target.value})}
                    />
                  </Col>
                </Row>
              </div>

              <div className="border-top pt-4">
                <div className="fw-bold text-primary mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.RESUME.TITLE'})}{' '}
                  {plageForm.debutDate !== '' && plageForm.debutHeure !== '' && (
                    <span className="text-muted fs-7 fw-normal">
                      {intl.formatMessage(
                        {id: 'TELEPHONIE.PBX.PLAGES.DEPUIS'},
                        {
                          date: formatDepuis(
                            `${plageForm.debutDate}T${plageForm.debutHeure}:00`,
                            intl.locale
                          ),
                        }
                      )}
                    </span>
                  )}
                </div>
                {plageForm.periodicite === 'semaine' && <JoursChips jours={plageForm.jours} />}
                <div className="mt-3">
                  <TimelinePlage periodes={periodesValides} />
                </div>
              </div>
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setShowPlageForm(false)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
            disabled={!plageFormValide}
            onClick={enregistrerPlage}
          />
        </Modal.Footer>
      </Modal>

      <Modal show={plageASupprimer != null} onHide={() => setPlageASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>{intl.formatMessage({id: 'TELEPHONIE.PBX.PLAGES.PLAGE_DELETE.BODY'})}</Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setPlageASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppressionPlage}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlagesHoraires;
